import * as React from 'react';
import _ from 'lodash';

import {
  ACTION_BUSINESS_OFFERING_CLICKED,
  CATEGORY_BUSINESS_PAGE,
} from 'utils/analytics/eventTypesConstants';
import {
  ACTION_BUSINESS_SELECT_EXPERIENCE,
  EventCustomAttributes,
} from 'utils/analytics/eventTypeConstantsAmplitude';
import { AmexIcon } from '@tock/shared/components/icons/CreditCardIcons';
import { Carousel, ExpandableParagraph, Typography } from 'omakase';
import { DisplayDate, DisplaySchedule, DisplayTime } from '@tock/time';
import { ExperienceOfferingDetailsLabel } from 'components/business/offering/ExperienceOfferingDetailsLabel';
import { IconClock, IconLogoAmericanExpress, IconTicket } from '@tock/icons';
import { IconClock as IconClockLegacy, IconEvents, IconPeople } from 'ui/icons';
import { IconMigrationUtil, useIsSakeIconSetEnabled } from 'omakase/utils/IconMigrationUtil';
import { LocalDate, LocalDateTime, ZoneOffset } from 'js-joda';
import { OfferingDetailsFees } from 'components/business/offering/OfferingDetailsFees';
import { OfferingDetailsPrice } from 'components/business/offering/OfferingDetailsPrice';
import { Theme, css } from '@emotion/react';
import { consumer, core } from '@tock/coretypes';
import { experienceOfferingToData } from 'utils/analytics/amplitude';
import { getAutoFormatSrcAndSrcSetForWidthPx } from '@tock/images';
import {
  getRemainingEventTicketText,
  isOfferedOnOneDateAndAtOneTime,
  isOfferingEventType,
  isOfferingGAEventType,
  isOfferingSeatedEventType,
} from 'utils/offerings';
import { getSizeContent, getTypeContent } from 'utils/experience';
import { useAmplitudeSendEvent } from 'utils/analytics/AnalyticsContext';
import { useHistory } from 'react-router-dom';
import Location from 'components/business/location/Location';
import OfferingDetailsAdditionalCosts from 'components/business/offering/OfferingDetailsAdditionalCosts';
import OfferingDetailsMetadataItem from 'components/business/offering/OfferingDetailsMetadataItem';
import OfferingHeading from 'components/business/offering/OfferingHeading';

type ClickContext = EventCustomAttributes['clickContext'];

interface Props {
  business: consumer.IBusinessConfig;
  cutoffLength?: number;
  isShowingOnBusinessPage?: boolean;
  label?: string;
  linkTo?: string;
  offering: consumer.Offering;
  onClick?(): void;
  onNavigateToExperience?(event: consumer.Offering, nextAvailableDate?: string): void;
  remainingTickets?: number;
  listIndex?: number;
  showExperienceImages?: boolean;
  isReschedule?: boolean;
}

const EventOfferingDetails: React.FC<Props> = ({
  onNavigateToExperience,
  offering,
  cutoffLength,
  linkTo,
  onClick,
  label,
  business,
  isShowingOnBusinessPage,
  remainingTickets,
  listIndex,
  showExperienceImages = true,
  isReschedule,
}) => {
  const amplitudeSendEvent = useAmplitudeSendEvent();
  const isSakeIconSetEnabled = useIsSakeIconSetEnabled();

  const history = useHistory();
  // Used in Search Modal when clicking on an experience name
  const handleNavigateToExperience = () => {
    if (onNavigateToExperience) {
      onNavigateToExperience(offering);
    }
  };

  const onClickTarget = (target: ClickContext) => {
    window.analytics.sendEvent(CATEGORY_BUSINESS_PAGE, ACTION_BUSINESS_OFFERING_CLICKED);
    amplitudeSendEvent(ACTION_BUSINESS_SELECT_EXPERIENCE, {
      listIndex,
      clickContext: target,
      ...experienceOfferingToData(offering),
    });
  };

  const getSingleDateTimeDisplay = (
    offering: consumer.IOffering,
    eventDetails: consumer.EventDetails
  ): React.ReactNode => {
    const { isAtOneTime } = offering;
    const { startTime, endTime, eventFirstDatetimeSecs } = eventDetails;
    const eventFirstDatetime = eventFirstDatetimeSecs
      ? LocalDateTime.ofEpochSecond(eventFirstDatetimeSecs, ZoneOffset.UTC)
      : null;
    if (startTime) {
      return (
        <>
          {!isAtOneTime && <div>Multiple times, starting at</div>}
          <DisplayTime timeValue={startTime} />
          {!_.isNil(endTime) && (
            <span>
              {' '}
              - <DisplayTime timeValue={endTime} />
            </span>
          )}
        </>
      );
    }

    if (eventFirstDatetime) {
      return (
        <>
          <DisplayTime timeValue={eventFirstDatetime} />
        </>
      );
    }

    return null;
  };

  const getMultiDateTemplateTimeDisplay = (
    offering: consumer.IOffering,
    eventDetails: consumer.EventDetails
  ): React.ReactNode => {
    const { isAtOneTime } = offering;
    const { startTime, endTime } = eventDetails;
    if (isAtOneTime) {
      return (
        <>
          <DisplayTime timeValue={startTime} />
          {!_.isNil(endTime) && (
            <span>
              {' '}
              - <DisplayTime timeValue={endTime} />
            </span>
          )}
        </>
      );
    }
    return <>Multiple times</>;
  };

  const {
    description,
    descriptiveVariety,
    eventDetails,
    isAmexOnly,
    isAtBusinessLocation,
    isOnOneDate,
    price,
    singleUnitQuantity,
    purchaseSize,
    type,
    image,
  } = offering;

  const showLegacyFeeDisplay = price?.priceDisplayStyle === 'BASE_TICKET_PRICE';
  const showItemizedFeeDisplay = price?.priceDisplayStyle === 'ITEMIZED';

  if (!isOfferingEventType(offering) || _.isNil(eventDetails)) {
    return null;
  }

  const { date, location } = eventDetails;
  const schedule = eventDetails.schedule?.[0];

  const navigate = !!onNavigateToExperience;

  const timeRange = isOnOneDate
    ? getSingleDateTimeDisplay(offering, eventDetails as consumer.EventDetails)
    : getMultiDateTemplateTimeDisplay(offering, eventDetails as consumer.EventDetails);

  // The second line of this can be removed when the old, non-specific EVENT variety is fully removed
  const showEventSize =
    isOfferingSeatedEventType(offering) ||
    (!isOfferingGAEventType(offering) && !isOfferedOnOneDateAndAtOneTime(offering));
  const remainingTicketsText = remainingTickets
    ? getRemainingEventTicketText(remainingTickets)
    : null;

  const dataTestIdValue = offering.name.replace(/\W/g, '');

  const carouselSlides =
    image.length > 0
      ? image.map((i: core.IUploadedImage) => {
          const { src, srcSet } = getAutoFormatSrcAndSrcSetForWidthPx(
            { width: 600 },
            i.imageBackingUrl ?? undefined
          );
          return { src, srcSet, alt: i.altText ?? '', type: 'image' as const };
        })
      : undefined;

  const shouldShowCarousel =
    showExperienceImages && isShowingOnBusinessPage && image.length > 0 && carouselSlides;

  const getCarouselOnClick = () => {
    if (navigate && !linkTo) {
      return () => {
        handleNavigateToExperience();
        onClickTarget('Image');
      };
    }
    if (!navigate && linkTo) {
      return () => {
        onClick && onClick();
        onClickTarget('Image');
        history.push(linkTo);
      };
    }
    return undefined;
  };

  const carouselOnClick = getCarouselOnClick();

  return (
    <div className="Consumer-resultsExperienceDetails">
      <div
        css={(theme: Theme) => [
          shouldShowCarousel &&
            css`
              display: flex;
              flex-direction: column;
              ${theme.breakpoints.up('sm')} {
                display: grid;
                grid-template-columns: 1fr minmax(250px, 0.75fr);
                column-gap: 30px;
              }
            `,
        ]}
      >
        <div className="Consumer-reservationInfo">
          <div>
            <ExperienceOfferingDetailsLabel offering={offering} />
            <OfferingHeading
              isShowingOnBusinessPage={isShowingOnBusinessPage}
              offering={offering}
              navigate={navigate}
              linkTo={linkTo}
              dataTestIdValue={dataTestIdValue}
              onClickTarget={onClickTarget}
              onClick={onClick}
              onNavigateToExperience={onNavigateToExperience}
              label={label}
            />
            {description && (
              <ExpandableParagraph
                paragraphText={description}
                linkify={true}
                cutoffLength={cutoffLength}
              />
            )}
          </div>

          <ul className="Consumer-reservationMetaList">
            {isAmexOnly && (
              <li className="Consumer-reservationMetaItem">
                <div className="Consumer-reservationMetaItemIcon">
                  <IconMigrationUtil
                    legacyIcon={
                      <AmexIcon contextClassName="Consumer-reservationMetaItemIcon-amexExclusive" />
                    }
                    newIcon={<IconLogoAmericanExpress />}
                  />
                </div>
                <Typography color="inherit" component="span" variant="body02">
                  American Express® Card exclusive
                </Typography>
              </li>
            )}

            {showEventSize && (
              <OfferingDetailsMetadataItem icon={isSakeIconSetEnabled ? IconTicket : IconPeople}>
                {getTypeContent(type, purchaseSize, descriptiveVariety, singleUnitQuantity)}
                {getSizeContent(type, purchaseSize)}
              </OfferingDetailsMetadataItem>
            )}

            {!_.isNil(schedule) && (
              <OfferingDetailsMetadataItem icon={isSakeIconSetEnabled ? IconTicket : IconEvents}>
                <DisplaySchedule
                  schedule={schedule as core.Schedule}
                  filterDatesEarlierThan={LocalDate.now()}
                />
              </OfferingDetailsMetadataItem>
            )}
            {_.isNil(schedule) && date && (
              <OfferingDetailsMetadataItem icon={isSakeIconSetEnabled ? IconTicket : IconEvents}>
                <DisplayDate dateValue={date} displayMonthAs="short" showWeekday />
              </OfferingDetailsMetadataItem>
            )}

            {timeRange && (
              <OfferingDetailsMetadataItem
                icon={isSakeIconSetEnabled ? IconClock : IconClockLegacy}
              >
                {timeRange}
              </OfferingDetailsMetadataItem>
            )}
            {type && (
              <OfferingDetailsPrice
                price={price as consumer.OfferedPriceSummary}
                type={type}
                descriptiveVariety={descriptiveVariety}
                singleUnitQuantity={singleUnitQuantity}
                isReschedule={isReschedule}
              />
            )}
            {type && showLegacyFeeDisplay && (
              <OfferingDetailsAdditionalCosts
                price={price as consumer.OfferedPriceSummary}
                type={type}
              />
            )}
            {showItemizedFeeDisplay && (price?.fees ?? []).length > 0 && (
              <OfferingDetailsFees fees={price?.fees ?? []} />
            )}
          </ul>

          {remainingTicketsText && (
            <p className="Consumer-reservationMetaList">
              <Typography color="inherit" component="span" variant="body02">
                {remainingTicketsText}
              </Typography>
            </p>
          )}

          {location && !isShowingOnBusinessPage && !isAtBusinessLocation && (
            <Typography color="inherit" component="span" variant="body02">
              <Location
                location={location as consumer.Location}
                business={business}
                isAtBusinessLocation={!!isAtBusinessLocation}
              />
            </Typography>
          )}
        </div>

        {shouldShowCarousel && (
          <div
            css={({ spacing, breakpoints }: Theme) => css`
              position: relative;
              aspect-ratio: 1.75 / 1;
              ${breakpoints.down('xs')} {
                order: -1;
                margin-bottom: ${spacing(2)}px;
              }
            `}
          >
            <Carousel
              slideClickHandling={
                carouselOnClick
                  ? {
                      onClick: carouselOnClick,
                      role: 'link',
                      'aria-label': 'Book now (opens a dialog)',
                    }
                  : undefined
              }
              slides={carouselSlides}
              showArrowsDesktop="onmouseover"
            />
          </div>
        )}
      </div>
    </div>
  );
};

export default EventOfferingDetails;
